import { computed, type ComputedRef, type Ref } from "vue";
import type { ScopeDescriptor, ScopeNode } from "@/types/bbel-types";
import type { BbelFunctionInfo } from "@/types/bbel-types";
import type { TemplateAuthMethodContent } from "@/types";
import {
  fixedScope,
  leaf,
  openScope,
  ORG_MEMBERS,
  RESPONSE_MEMBERS,
  SYSTEM_MEMBERS,
} from "@/views/builder/composables/useBbelScope";

/**
 * The evaluation phase a template expression runs in — each binds a different scope set (mirrors
 * the server's `TemplatePhase`): `request` = a handler's pre-call shape (no `response`);
 * `response` = onResponse values (everything); `outbound` = the per-call surfaces (baseUrl,
 * outbound headers/query), long after setup, so `flow` / `inbound` / `response` are gone.
 */
export type TemplatePhase = "request" | "response" | "outbound";

/** One ScopeDescriptor per phase — the shape the method and flow forms thread to their fields. */
export interface TemplateScopes {
  request: ScopeDescriptor;
  response: ScopeDescriptor;
  outbound: ScopeDescriptor;
}

/**
 * Template-authoring scope for the BBEL editor — one auth method's contract for one phase,
 * mirroring the server's `ConnectionTemplateScopeSchema` (the publish-time authority; a lag here
 * only costs a suggestion):
 *
 * - `connection` is member-strict — the method's declared fields plus the engine-written token
 *   fields. Secrets resolve here (auth handlers run server-side and exist to use them), so nothing
 *   is locked — the workflow context's #6010 lock does not apply.
 * - `platform` offers the method's declared variables but stays open (dotted, namespaced names
 *   bind at runtime).
 * - `response` / `system` / `org` are the fixed engine shapes; `flow` / `inbound` / `credentials`
 *   are runtime-bound and open.
 * - `services` / `wizard` / `datastore` are absent — a template never binds them, and referencing
 *   one fails as an unknown scope.
 */
export function buildConnectionTemplateScope(
  method: TemplateAuthMethodContent | null | undefined,
  phase: TemplatePhase,
): ScopeNode[] {
  const connectionFields = [
    ...(method?.connection ?? []).filter((f) => f.name.trim()).map((f) => leaf("connection", f.name)),
    { ...leaf("connection", "accessToken"), hint: "Engine-written token field" },
    { ...leaf("connection", "refreshToken"), hint: "Engine-written token field" },
  ];
  const connection: ScopeNode = {
    segment: "connection",
    path: "connection",
    label: "connection",
    closed: true,
    children: connectionFields,
  };

  const platform: ScopeNode = {
    ...openScope("platform"),
    ...(method?.platform?.length
      ? { children: method.platform.filter((v) => v.name.trim()).map((v) => leaf("platform", v.name)) }
      : {}),
  };

  const perCall = [connection, platform, fixedScope("system", SYSTEM_MEMBERS), fixedScope("org", ORG_MEMBERS)];
  if (phase === "outbound") return [...perCall, openScope("credentials")];

  const request = [...perCall, openScope("flow"), openScope("inbound"), openScope("credentials")];
  if (phase === "request") return request;
  return [...request.slice(0, 2), fixedScope("response", RESPONSE_MEMBERS), ...request.slice(2)];
}

/** Reactive per-phase scope descriptors for one auth method's BBEL fields. */
export function useConnectionTemplateScopes(
  method: Ref<TemplateAuthMethodContent | null | undefined>,
  functions: Ref<BbelFunctionInfo[]> | ComputedRef<BbelFunctionInfo[]>,
) {
  const descriptor = (phase: TemplatePhase): ScopeDescriptor => ({
    roots: buildConnectionTemplateScope(method.value, phase),
    functions: functions.value,
  });
  return computed<TemplateScopes>(() => ({
    request: descriptor("request"),
    response: descriptor("response"),
    outbound: descriptor("outbound"),
  }));
}
